import React from 'react'
import './App.css'
import { Route, BrowserRouter, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider, setLogger } from 'react-query'

import { AppContainer } from './components/AppContainer/AppContainer'
import { TranslationProvider } from '@local/translations'
import { SnackBarProvider } from '@toasttab/buffet-pui-snackbars'
import { useIntlProps, useSentry } from 'banquet-runtime-modules'
import { AuthProvider } from '@toasttab/do-secundo-guest-authentication'
import { baseAuthUrl } from './api/guest/gateway'
import { RestaurantProvider } from './contexts/RestaurantContext'
import { Button } from '@toasttab/buffet-pui-buttons'

const shouldUseDevTools = process.env.NODE_ENV === 'development'
const LazyLoadInDevelopment = <T extends React.ComponentType<any>>(
  loadComponent: () => Promise<{ default: T }>
) => {
  return shouldUseDevTools ? React.lazy(loadComponent) : () => null
}

const ReactQueryDevtoolsPanel = LazyLoadInDevelopment<
  typeof import('react-query/devtools').ReactQueryDevtoolsPanel
>(() =>
  import('react-query/devtools').then((res) => ({
    default: res.ReactQueryDevtoolsPanel
  }))
)

export const queryClient = new QueryClient()

export function App() {
  const i18n = useIntlProps()
  // When available, load this from Banquet
  const cdnUrl =
    '//cdn.toasttab.com/static/34d9bc8b2e7d99006ecfcd8a8ba5e3dc5c75d126/projects/intgs/translations'
  const [openDevTools, setOpenDevTools] = React.useState(false)

  const sentry = useSentry()
  React.useEffect(() => {
    // Add sentry logging to React Query errors
    setLogger({
      log: (message) => {
        // eslint-disable-next-line no-console
        console.log(message)
      },
      warn: (message) => {
        sentry.captureMessage(message)
      },
      error: (error) => {
        sentry.captureException(
          new Error(
            `${error.config?.method.toUpperCase()} ${
              error.config?.url
            } failed due to ${error?.response?.status || error?.status}: ${
              error?.response?.data?.message
            }`
          ),
          {
            captureContext: {
              extra: {
                request: {
                  url: error.config?.url,
                  method: error.config?.method,
                  headers: error.config?.headers
                }
              }
            }
          }
        )
      }
    })
  }, [sentry])

  return (
    <TranslationProvider i18n={i18n.i18next} cdnUrl={cdnUrl}>
      <SnackBarProvider>
        <QueryClientProvider client={queryClient}>
          {shouldUseDevTools && (
            <React.Suspense>
              <div className='relative z-[9999]'>
                <div className='absolute w-full'>
                  {!openDevTools && (
                    <Button
                      className='absolute top-0 left-0 m-8'
                      onClick={() => setOpenDevTools((v) => !v)}
                    >
                      {openDevTools
                        ? 'Close React Query Dev Tools'
                        : 'Open React Query Dev Tools'}
                    </Button>
                  )}
                  <div
                    className='data-[open=false]:hidden'
                    data-open={openDevTools}
                  >
                    <ReactQueryDevtoolsPanel
                      handleDragStart={() => {
                        setOpenDevTools(true)
                      }}
                      isOpen={openDevTools}
                      setIsOpen={setOpenDevTools}
                    />
                  </div>
                </div>
              </div>
            </React.Suspense>
          )}
          <AuthProvider gatewayOrigin={baseAuthUrl}>
            <RestaurantProvider
              restaurantGuid={window.TT_GLOBALS.restaurantGuid}
            >
              <BrowserRouter>
                <Routes>
                  <Route
                    path='/restaurants/:restaurantGuid/*'
                    element={<AppContainer />}
                  />
                  <Route path='/:shortUrl/*' element={<AppContainer />} />
                </Routes>
              </BrowserRouter>
            </RestaurantProvider>
            <div id='banquetPortalsContainer' />
          </AuthProvider>
        </QueryClientProvider>
      </SnackBarProvider>
    </TranslationProvider>
  )
}
